import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import Header from '../../components/Header';
import { StripeCheckoutModal } from '../../components/StripeCheckout';
import { TrustBadges, StripeSecurityBadge, useTrustStats, AnimatedCounter } from '../../components/TrustSignals';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, MapPin, Tag, User, MessageCircle, Send, 
  Package, Loader2, Trash2, Edit, ShoppingCart, Clock, Star, Plus, Shield, Users, CheckCircle, Lock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const conditions = {
  new: { label: 'New', color: 'bg-green-100 text-green-700' },
  used: { label: 'Used', color: 'bg-gray-100 text-gray-700' },
  refurbished: { label: 'Refurbished', color: 'bg-blue-100 text-blue-700' }
};

const categories = {
  auto_parts: 'Auto Parts',
  tools: 'Tools & Equipment',
  vehicles: 'Vehicles',
  accessories: 'Accessories',
  other: 'Other'
};

export default function ProductDetail() {
  const { productId } = useParams();
  const { user, getAuthHeaders } = useAuth();
  const { addToCart } = useCart();
  const navigate = useNavigate();
  
  const [product, setProduct] = useState(null);
  const [sellerInfo, setSellerInfo] = useState(null);
  const [comments, setComments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState(null);

  useEffect(() => {
    fetchProduct();
    fetchComments();
  }, [productId]);

  useEffect(() => {
    if (product?.seller_id) {
      fetchSellerInfo(product.seller_id);
    }
  }, [product?.seller_id]);

  useEffect(() => {
    if (user && showMessages) {
      fetchMessages();
    }
  }, [showMessages, user]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/marketplace/products/${productId}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Failed to fetch product:', error);
      toast.error('Product not found');
      navigate('/marketplace');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API}/marketplace/products/${productId}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    }
  };

  const fetchSellerInfo = async (sellerId) => {
    try {
      const response = await axios.get(`${API}/marketplace/seller/${sellerId}`);
      setSellerInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch seller info:', error);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(
        `${API}/marketplace/products/${productId}/messages`,
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !user) return;

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/marketplace/products/${productId}/comments`,
        { content: newComment },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setNewComment('');
      fetchComments();
      toast.success('Comment added');
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !user) return;

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/marketplace/products/${productId}/messages`,
        { content: newMessage },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setNewMessage('');
      fetchMessages();
      toast.success('Message sent');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePurchase = async () => {
    if (!user) {
      toast.error('Please login to purchase');
      navigate('/login');
      return;
    }

    setPurchasing(true);
    try {
      // Create purchase/order
      const createResponse = await axios.post(
        `${API}/marketplace/purchase`,
        { product_id: productId },
        { headers: getAuthHeaders(), withCredentials: true }
      );

      // Store order ID and open payment modal
      setCurrentOrderId(createResponse.data.purchase_id || createResponse.data.order_id);
      setShowPaymentModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start purchase');
    } finally {
      setPurchasing(false);
    }
  };

  const handlePaymentSuccess = async () => {
    setShowPaymentModal(false);
    toast.success('Purchase completed! The seller has been notified.');
    fetchProduct();
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this listing?')) return;

    try {
      await axios.delete(
        `${API}/marketplace/products/${productId}`,
        { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success('Listing removed');
      navigate('/marketplace');
    } catch (error) {
      toast.error('Failed to delete listing');
    }
  };

  const handleAddToCart = () => {
    addToCart({
      id: product.product_id,
      type: 'product',
      name: product.title,
      price: product.price,
      image: product.images?.[0],
      seller_id: product.seller_id,
      seller_name: product.seller_name
    });
    toast.success('Added to cart!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-[70vh]">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
        </div>
      </div>
    );
  }

  if (!product) return null;

  const isOwner = user?.user_id === product.seller_id;
  const isSold = product.status === 'sold';
  const platformFee = (product.price * 0.01).toFixed(2);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="product-detail-page">
      <Header />
      
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button 
          onClick={() => navigate('/marketplace')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Marketplace
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Product Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Images */}
            <Card className="overflow-hidden">
              <div className="h-96 bg-gray-100 flex items-center justify-center">
                {product.images && product.images.length > 0 ? (
                  <img 
                    src={product.images[0]} 
                    alt={product.title}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <Package className="w-24 h-24 text-gray-400" />
                )}
              </div>
            </Card>

            {/* Details */}
            <Card>
              <CardContent className="py-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 font-['Chivo'] mb-2">
                      {product.title}
                    </h1>
                    <div className="flex items-center gap-3">
                      <span className={`text-sm px-3 py-1 rounded-full ${conditions[product.condition]?.color}`}>
                        {conditions[product.condition]?.label}
                      </span>
                      <span className="text-sm text-gray-500 flex items-center gap-1">
                        <Tag className="w-4 h-4" />
                        {categories[product.category]}
                      </span>
                    </div>
                  </div>
                  {isSold && (
                    <span className="bg-red-100 text-red-700 px-4 py-2 rounded-full font-semibold">
                      SOLD
                    </span>
                  )}
                </div>

                <p className="text-gray-700 mb-6 whitespace-pre-wrap">{product.description}</p>

                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin className="w-5 h-5" />
                  <span>{product.location}</span>
                </div>
              </CardContent>
            </Card>

            {/* Comments Section */}
            <Card data-testid="comments-section">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  Comments ({comments.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {user ? (
                  <form onSubmit={handleComment} className="mb-6">
                    <div className="flex gap-3">
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={user.picture} />
                        <AvatarFallback>{user.name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <Textarea
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          placeholder="Write a comment..."
                          rows={2}
                          className="mb-2"
                          data-testid="comment-input"
                        />
                        <Button 
                          type="submit" 
                          size="sm"
                          disabled={submitting || !newComment.trim()}
                          data-testid="submit-comment"
                        >
                          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Post Comment'}
                        </Button>
                      </div>
                    </div>
                  </form>
                ) : (
                  <p className="text-gray-500 mb-4">
                    <Link to="/login" className="text-blue-600 hover:underline">Login</Link> to comment
                  </p>
                )}

                <div className="space-y-4">
                  {comments.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No comments yet</p>
                  ) : (
                    comments.map((comment) => (
                      <div key={comment.comment_id} className="flex gap-3" data-testid={`comment-${comment.comment_id}`}>
                        <Avatar className="w-10 h-10">
                          <AvatarImage src={comment.user_picture} />
                          <AvatarFallback>{comment.user_name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-gray-900">{comment.user_name}</span>
                            <span className="text-sm text-gray-500">
                              {new Date(comment.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-gray-700">{comment.content}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Price & Purchase */}
            <Card data-testid="purchase-card">
              <CardContent className="py-6">
                <div className="mb-4">
                  <span className="text-4xl font-bold text-blue-600 font-['Chivo']">
                    ${product.price.toFixed(2)}
                  </span>
                  <p className="text-sm text-gray-500 mt-1">
                    + ${platformFee} platform fee (1%)
                  </p>
                </div>

                {!isOwner && !isSold && (
                  <>
                    <Button 
                      className="w-full h-12 bg-blue-600 hover:bg-blue-700 mb-3"
                      onClick={handlePurchase}
                      disabled={purchasing}
                      data-testid="buy-now-button"
                    >
                      {purchasing ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <ShoppingCart className="w-5 h-5 mr-2" />
                          Buy Now
                        </>
                      )}
                    </Button>
                    <Button 
                      variant="outline"
                      className="w-full mb-3"
                      onClick={handleAddToCart}
                      data-testid="add-to-cart-button"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add to Cart
                    </Button>
                  </>
                )}

                {!isOwner && user && !isSold && (
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => setShowMessages(!showMessages)}
                    data-testid="message-seller-button"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Message Seller
                  </Button>
                )}

                {/* Trust Signals */}
                {!isSold && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Shield className="w-4 h-4 text-green-600" />
                        <span>Buyer Protection Guaranteed</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Lock className="w-4 h-4 text-blue-600" />
                        <span>Secure Stripe Checkout</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>Verified Seller</span>
                      </div>
                    </div>
                    <div className="mt-4">
                      <StripeSecurityBadge />
                    </div>
                  </div>
                )}

                {isOwner && (
                  <div className="space-y-3">
                    <Link to={`/marketplace/edit/${productId}`}>
                      <Button variant="outline" className="w-full">
                        <Edit className="w-4 h-4 mr-2" />
                        Edit Listing
                      </Button>
                    </Link>
                    <Button 
                      variant="outline" 
                      className="w-full text-red-600 border-red-200 hover:bg-red-50"
                      onClick={handleDelete}
                      data-testid="delete-listing"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Listing
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Seller Info */}
            <Card data-testid="seller-card">
              <CardContent className="py-6">
                <h3 className="text-sm font-medium text-gray-500 mb-3">SELLER</h3>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
                    {sellerInfo?.picture ? (
                      <img src={sellerInfo.picture} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-6 h-6 text-gray-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{product.seller_name}</p>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      {sellerInfo?.rating && (
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                          <span>{sellerInfo.rating}</span>
                        </div>
                      )}
                      <span>•</span>
                      <span>{sellerInfo?.total_sales || 0} sales</span>
                    </div>
                  </div>
                </div>
                {sellerInfo?.general_location && (
                  <div className="text-sm text-gray-500 flex items-center gap-1 mb-2">
                    <MapPin className="w-4 h-4" />
                    {sellerInfo.general_location}
                  </div>
                )}
                <div className="text-sm text-gray-500 flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Listed {new Date(product.created_at).toLocaleDateString()}
                </div>
              </CardContent>
            </Card>

            {/* Messages */}
            {showMessages && user && (
              <Card data-testid="messages-card">
                <CardHeader>
                  <CardTitle className="text-lg">Messages</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="max-h-64 overflow-y-auto mb-4 space-y-3">
                    {messages.length === 0 ? (
                      <p className="text-gray-500 text-sm">No messages yet</p>
                    ) : (
                      messages.map((msg) => (
                        <div 
                          key={msg.message_id}
                          className={`p-3 rounded-lg ${
                            msg.sender_id === user.user_id 
                              ? 'bg-blue-100 ml-4' 
                              : 'bg-gray-100 mr-4'
                          }`}
                        >
                          <p className="text-sm font-medium">{msg.sender_name}</p>
                          <p className="text-sm text-gray-700">{msg.content}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(msg.created_at).toLocaleString()}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                  
                  <form onSubmit={handleMessage} className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      className="flex-1"
                      data-testid="message-input"
                    />
                    <Button type="submit" size="icon" disabled={submitting || !newMessage.trim()}>
                      <Send className="w-4 h-4" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      {/* Stripe Payment Modal */}
      <StripeCheckoutModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        amount={product ? product.price * 1.01 : 0}
        orderId={currentOrderId}
        itemName={product?.title}
        onSuccess={handlePaymentSuccess}
        paymentType="marketplace"
      />
    </div>
  );
}
